'use client';

import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, PackageSearch, Search, ShoppingCart, Smartphone } from 'lucide-react';
import { BottomNav } from '@/components/mobile/bottom-nav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCart } from '@/hooks/useCart';
import { useDeviceFinder } from '@/hooks/useDeviceFinder';
import { getBrandLogoSrc } from '@/lib/brand-logos';
import { buildDeviceUrl } from '@/lib/device-search';

/**
 * Native-app finder browser (`/devices` below `md`): sticky header with
 * cart access, live model search, 3-column brand tiles swapping in place
 * to a flat 2-column device image-card grid. Desktop tree is untouched.
 */
export function MobileDevicesView() {
  const {
    filteredBrands,
    visibleDevices,
    deviceCount,
    query,
    setQuery,
    selectedBrand,
    setSelectedBrand,
    selectedBrandName,
    reset,
    isEmpty,
  } = useDeviceFinder();
  const { itemCount } = useCart();

  const trimmedQuery = query.trim();

  return (
    <div dir="rtl" className="min-h-screen bg-background pb-24 md:hidden">
      <header className="sticky top-0 z-30 space-y-3 border-b border-border bg-background/95 px-4 pt-4 pb-3 backdrop-blur">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-xl font-black">اختر هاتفك</h1>
          <Link
            href="/order"
            aria-label="السلة"
            className="relative flex h-11 w-11 items-center justify-center rounded-full text-foreground"
          >
            <ShoppingCart className="h-6 w-6" />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
                {itemCount}
              </span>
            )}
          </Link>
        </div>
        <div className="relative">
          <Search className="absolute inset-s-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث عن موديل هاتفك..."
            aria-label="ابحث عن موديل هاتفك"
            className="h-12 bg-muted ps-10 text-base"
          />
        </div>
      </header>

      <main className="px-4">
        {selectedBrand === null ? (
          <section className="mt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-extrabold">
                {trimmedQuery ? `نتائج البحث عن «${trimmedQuery}»` : 'اختر علامة هاتفك'}
              </h2>
              <p className="text-xs font-bold text-muted-foreground">
                {filteredBrands.length} علامات
              </p>
            </div>

            {filteredBrands.length === 0 ? (
              <EmptyState
                title="لا توجد علامات مطابقة"
                description={
                  trimmedQuery
                    ? `لا توجد نتائج لـ «${trimmedQuery}». جرّب كلمة أخرى.`
                    : 'لا توجد علامات مسجلة بعد.'
                }
                onReset={reset}
              />
            ) : (
              <div className="mt-3 grid grid-cols-3 gap-3">
                {filteredBrands.map((brand) => (
                  <button
                    key={brand.slug}
                    type="button"
                    onClick={() => setSelectedBrand(brand.slug)}
                    className="flex min-h-11 flex-col items-center gap-2 rounded-2xl border border-border bg-card p-3 transition-colors active:bg-muted"
                  >
                    <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-muted p-2">
                      <Image
                        src={getBrandLogoSrc(brand.logoType)}
                        alt={brand.nameAr}
                        width={56}
                        height={56}
                        className="h-full w-full object-contain"
                      />
                    </span>
                    <span className="line-clamp-1 w-full text-center text-xs font-bold">
                      {brand.nameAr}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {trimmedQuery && filteredBrands.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-extrabold">موديلات مطابقة</h2>
                  <p className="text-xs font-bold text-muted-foreground">
                    {deviceCount} موديل
                  </p>
                </div>
                {visibleDevices.length === 0 ? (
                  <EmptyState
                    title="لا توجد موديلات مطابقة"
                    description={`لا توجد نتائج لـ «${trimmedQuery}». جرّب كلمة أخرى أو اختر علامة مختلفة.`}
                    onReset={reset}
                  />
                ) : (
                  <DeviceGrid devices={visibleDevices} />
                )}
              </div>
            )}
          </section>
        ) : (
          <section className="mt-4">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setSelectedBrand(null)}
                aria-label="رجوع إلى العلامات"
                className="flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-bold"
              >
                <ArrowRight className="h-5 w-5" />
                <span>{selectedBrandName || 'العلامات'}</span>
              </button>
              <p className="text-xs font-bold text-muted-foreground">
                {trimmedQuery ? (
                  <>نتائج «{trimmedQuery}» ({deviceCount})</>
                ) : (
                  <>{deviceCount} موديل</>
                )}
              </p>
            </div>

            {isEmpty ? (
              <EmptyState
                title="لا توجد موديلات مطابقة"
                description={
                  trimmedQuery
                    ? `لا توجد نتائج لـ «${trimmedQuery}» ضمن ${selectedBrandName}. جرّب كلمة أخرى.`
                    : 'لا توجد موديلات مسجلة لهذه العلامة بعد.'
                }
                onReset={reset}
              />
            ) : (
              <div className="mt-3">
                <DeviceGrid devices={visibleDevices} />
              </div>
            )}
          </section>
        )}

</main>

      <BottomNav />
    </div>
  );
}

function DeviceGrid({ devices }: { devices: ReturnType<typeof useDeviceFinder>['visibleDevices'] }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-3">
      {devices.map((device) => (
        <Link
          key={device.id}
          href={buildDeviceUrl(device.slug)}
          className="flex min-h-11 flex-col items-center gap-2 rounded-2xl border border-border bg-card p-3 text-center transition-colors active:bg-muted"
        >
          <span className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl ">
            {device.imageUrl ? (
              <Image
                src={device.imageUrl}
                alt={device.nameAr}
                width={160}
                height={160}
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <span className="flex flex-col items-center gap-1 text-muted-foreground">
                <Smartphone className="h-8 w-8" />
                <span className="text-2xl font-black">{device.nameAr.charAt(0)}</span>
              </span>
            )}
          </span>
          <span className="line-clamp-1 w-full text-xs font-extrabold">{device.nameAr}</span>
          {/* {device.nameEn ? (
            <span className="line-clamp-1 w-full text-[11px] text-muted-foreground" dir="ltr">
              {device.nameEn}
            </span>
          ) : null} */}
        </Link>
      ))}
    </div>
  );
}

function EmptyState({
  title,
  description,
  onReset,
}: {
  title: string;
  description: string;
  onReset: () => void;
}) {
  return (
    <div className="mt-3 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-12 text-center">
      <PackageSearch className="h-12 w-12 text-muted-foreground/50" />
      <p className="text-sm font-bold">{title}</p>
      <p className="max-w-sm text-xs text-muted-foreground">{description}</p>
      <Button type="button" onClick={onReset} variant="outline" className="mt-2 min-h-11 rounded-full px-6">
        إعادة ضبط البحث
      </Button>
    </div>
  );
}
